import React, { useState, forwardRef, useRef } from 'react'
import PropTypes from 'prop-types'
import Validators from './util/validators'
import Password from './util/password'

/**
 * @param {string|(key:string, value) => import('./interfaces').Validity} rules
 * @param {any} value
 * @param {string} key
 */
function applyRulesToValue (rules, value, key) {
  /** @type {import('./interfaces').Validity} */
  const valid = {
    valid: true,
    message: ''
  }

  switch (typeof rules) {
    case 'string':
      return rules.split('|').reduce((accumulatedValidation, rule) => {
        const [ruleKey, argsExpression = ''] = rule.split(':')
        const args = argsExpression.split('-')

        if (!accumulatedValidation.valid || !Validators.getRule(ruleKey)) {
          return accumulatedValidation
        }

        return Validators.validate(ruleKey, value, ...args)
      }, valid)
    case 'function':
      return rules(key, value)
    default:
      return valid
  }
}

/**
 * @template T
 * @template U
 * @param {import('./interfaces').WrappedComponent<T, U>} WrappedComponent
 */
function Formidable (WrappedComponent) {
  /**
   * @param {import('./interfaces').WrappedComponentProps<T, U>} props
   * @param {React.Ref<import('./interfaces').WrappedComponentRef<U>>} ref
   */
  function formidable (props, ref) {
    /** @type {import('react').RefObject<Record<string,import('./interfaces').MountedComponent>>} */
    const mountedRef = useRef({})
    const mounted = mountedRef.current
    /** @type {Record<string, any>} */
    const initialErrors = {}
    const [errors, setState] = useState(initialErrors)

    if (ref) {
      const refValue = {
        errors: () => ({ ...errors }),
        mounted,
        validateField,
        isFieldValid,
        mount,
        unmount,
        checkRefs,
        setError,
        clearErrors,
        validateForm,
        isValid,
        validate,
        setErrors,
        toJS
      }
      if (typeof ref === 'function') ref(refValue)
      else {
        ref.current = refValue
      }
    }

    return <WrappedComponent {...props} innerRef={ref} mount={mount} />

    /**
     * Internal method to run a validator set of rules against a value and a key
     * and perform a setError on the given field.
     * @param {string|() => import('./interfaces').Validity} rules
     * @param {*} value
     * @param {string} key
     * @returns {boolean}
     */
    function validateField (rules, value, key) {
      const isValid = applyRulesToValue(rules, value, key)
      setError(key, isValid.valid ? null : isValid.message)
      return isValid.valid
    }

    /**
     * Internal method to run a validator set of rules against a value and a key
     * @param {string|import('./interfaces').Validator} rules
     * @param {*} value
     * @param {string} key
     * @returns {boolean}
     */
    function isFieldValid (rules, value, key) {
      const isValid = applyRulesToValue(rules, value, key)
      return isValid.valid
    }

    /**
     * Helps mount an element by insuring required fields
     * @param {string} name
     * @param {Record<string, (...args: any)=> void>=} events
     * @param {string=} id
     */
    function mount (name, events = {}, id = name) {
      if (!Object.keys(events).length) {
        const { onBlur, onFocus, onChange } = props
        events = {
          onBlur,
          onFocus,
          onChange
        }
      }

      return Object.assign(
        {
          id,
          name,
          error: errors[name],
          /**
           * @param {import('./interfaces').MountedComponent} element
           */
          ref: element => {
            mounted[name] = element
          }
        },
        events
      )
    }

    /**
     * Removes an individual managed element
     * @param {string} name
     */
    function unmount (name) {
      // remove ref
      delete mounted[name]
      // clear errors that may be held
      setError(name, null)
    }

    /**
     * Checks if something has been unmounted so we don't keep a reference to it
     */
    function checkRefs () {
      for (const field in mounted) mounted[field] || unmount(field)
    }

    /**
     * Sets or removes an error on a managed field
     * @param {string} field
     * @param {*=} error
     */
    function setError (field, error) {
      if (error) {
        errors[field] = error
      } else {
        delete errors[field]
      }

      setState(Object.assign({}, errors))
    }

    /**
     * Sets or removes multiple error on a managed fields
     * @param {Record<string, any>} fields
     */
    function setErrors (fields) {
      for (const field in fields) {
        const error = fields[field]

        if (error) {
          errors[field] = error
        } else {
          delete errors[field]
        }
      }

      setState(Object.assign({}, errors))
    }

    /**
     * Resets local state and clears all errors
     */
    function clearErrors () {
      for (const field in mounted) {
        delete errors[field]
      }

      setState({})
    }

    /**
     * Validate a field or the whole form is field is not supplied
     * @param {string=} field
     * @param {*=} value
     * @param {boolean=} shouldSetErrors
     */
    function validateForm (field, value, shouldSetErrors) {
      const validatingFunction = shouldSetErrors ? validateField : isFieldValid

      if (field) {
        return __validate(field, value)
      } else {
        let isValid = true
        checkRefs()

        for (const field in mounted) {
          const component = mounted[field]
          const { element, type } = component

          if (type === 'checkbox' || type === 'radio') {
            value = element.checked
          } else if (element && value in element) {
            value = element.value
          } else {
            value = component.value
          }

          isValid = __validate(field, value) && isValid
        }
        return isValid
      }

      /**
       * @param {string} field
       * @param {*} value
       */
      function __validate (field, value) {
        const component = mounted[field]

        // unmounted ?
        if (!component) return false

        if (value == null) {
          value =
            component.value ||
            (component.state && component.state.value) ||
            value
        }

        const rules =
          component.rules ||
          (component.props && component.props.rules) ||
          (component.attributes &&
            component.attributes.rules &&
            component.attributes.rules.value)
        return !!validatingFunction(rules, value, field)
      }
    }

    /**
     * Validates either a field/value pair or the whole form
     * @param {string=} field
     * @param value
     * @returns {boolean}
     */
    function isValid (field, value) {
      return validateForm(field, value, false)
    }

    /**
     * Validates either a field/value pair or the whole form
     * and performs a setError on fields
     * @param {string=} field
     * @param value
     * @returns {boolean}
     */
    function validate (field, value) {
      return validateForm(field, value, true)
    }

    /**
     * Serialize all mounted fields only based upon current values
     * @returns {Record<string,any>}
     */
    function toJS () {
      const data = {}
      // protect against unmounted stale refs
      checkRefs()
      for (const field in mounted) {
        data[field] = mounted[field].value
      }
      return data
    }
  }

  const FormidableComp = forwardRef(formidable)
  FormidableComp.displayName = 'Formidable'
  FormidableComp.propTypes = {
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func
  }

  return FormidableComp
}

export { Formidable, Validators, Password }
